import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "py-2 w-full" }

import { onMounted, ref, watch } from 'vue';
import {
  Coverage,
  InsurancePolicy,
  IReviewSummary,
  ISitecoreFormGenericInput,
  ISitecoreItem,
  ISitecoreReviewSummaryItem,
  PetCoverage,
  ResidentialCoverage,
  VehicleCoverage,
} from '@/interfaces';
import { getFormInput } from '@/utils/form-input-helper';
import { useI18n } from 'vue-i18n';
import { replaceBookmark } from '@/utils/bookmark.replace';
import ReviewSummary from '@/components/ReviewSummary/ReviewSummary.vue';
import { SSInsuranceProductDetailsPolicyDetailsRules } from '@/utils/display-rules/ss-insurance-product-details-policy-details-rules';
import SSPolicyDiscounts from '@/components/SSPolicyDiscounts/SSPolicyDiscounts.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { TagSize } from '@/enums/tag-enums';
import { mapAsResidentialCoverage } from './PolicyDetailsResidentialCoverageMapper';
import { InformationBoxTypes, Link, LinkTypes } from '@/interfaces/forms/information-box.interface';
import {
  isComprehensiveCarOrIsFireTheftAndThirdPartyCarOrIsThirdPartyPropertyDamageCar,
  isUpgradeable,
  mapAsMotorCoverage,
  updatePrimaryLinkQueryString,
  updateQuoteSessionId,
} from './PolicyDetailsMotorCoverageMapper';
import { executeEditRules } from '@/utils/edit-rules/execute-rules';
import { SSInsuranceProductDetailsPolicyEditRules } from '@/utils/edit-rules/ss-insurance-product-details-policy-edit-rules';
import { SelfServiceSessionPages } from '@/enums/self-service-session-pages';
import { mapAsPetCoverage } from './PolicyDetailsPetCoverageMapper';
import { numberToWord } from '@/utils/text-transforms';
import { hasSameProperty } from '@/utils/common-helper';

interface PolicyDetailsData {
  reviewSummary: IReviewSummary[];
  reviewSummaryDiscounts: string[];
  reviewSummaryContent: any;
  showUpgradeInfoBoxForMotor: boolean;
  upgradeToComprehensiveLink: Link | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductDetailsPolicyDetails',
  props: {
    content: {},
    currentPolicyTemp: {},
    selfServiceToggles: {}
  },
  setup(__props: any) {

const props = __props;

const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;

const currentPolicy = ref<InsurancePolicy>();
const upgradeToComprehensiveInfoBox = ref<any>(getFormInput(props.content, 'upgradeToComprehensiveInfoBox'));
const upgradeToComprehensiveLinkItem = ref<any>(getFormInput(props.content, 'upgradeToComprehensiveLink'));

const policyDetailsData = ref<PolicyDetailsData[]>([]);

onMounted(async () => {
  currentPolicy.value = props.currentPolicyTemp; // hand off the prop to the reactive currentPolicy
});

watch(currentPolicy, async () => {
  if (currentPolicy.value) {
    await getPolicyDetailsSummaryModel();
  }
});


async function executeDisplayRules(
  ruleSet: Record<string, (details: any) => boolean | Promise<boolean>>,
  rules: ISitecoreItem[],
  details: ResidentialCoverage | VehicleCoverage | PetCoverage
): Promise<boolean> {
  if (rules && rules.length > 0) {
    const results = await Promise.all(
      rules.map(async (rule) => {
        const ruleFunc = ruleSet[rule.name];
        if (typeof ruleFunc === 'function') {
          return await ruleFunc(details);
        } else {
          console.warn(`Rule function not found for: ${rule.name}`);
          return false;
        }
      })
    );

    return results.every(Boolean);
  }

  return true;
}


/**
 * Returns the content for the ReviewSummaryItems and the formatted coverage (Residential, Vehicle, Pet).
 */
async function formatCoverageAsSpecific(
  genericCoverage: Coverage
): Promise<[any, ResidentialCoverage | VehicleCoverage | PetCoverage]> {
  let summary = {} as any;
  let coverage: ResidentialCoverage | VehicleCoverage | PetCoverage;

  if (genericCoverage.isHome) {
    coverage = genericCoverage as ResidentialCoverage;
    summary = await mapAsResidentialCoverage(coverage, props.content, dictionary);
  } else if (genericCoverage.isPet) {
    coverage = genericCoverage as PetCoverage;
    summary = await mapAsPetCoverage(coverage, props.content, dictionary);
  } else {
    // isMotor Treat coverage as 'other' (motorcycle, boat, caravan, etc)
    coverage = genericCoverage as VehicleCoverage;
    summary = await mapAsMotorCoverage(coverage, props.content, dictionary);
  }
  return [summary, coverage];
}

async function mapCoverage(genericCoverage: Coverage, summaryFields: any): Promise<IReviewSummary[]> {
  // Retrieve the formatted coverage (Residential, Vehicle, Pet) and content for the ReviewSummaryItems
  const [summary, coverage] = await formatCoverageAsSpecific(genericCoverage);

  let raw = JSON.stringify(summaryFields);
  Object.keys(summary).forEach((key) => {
    raw = replaceBookmark(key, raw, (summary as any)[key]);
  });
  summaryFields = JSON.parse(raw);

  // Need Promise.all() because calling an async method and ReviewSummary needs hard values
  
  const x = await Promise.all(
    summaryFields.map(async (field: any) => {
      const itemChecks = await Promise.all(
        field.items.map(async (item: ISitecoreReviewSummaryItem) => {
          let showItem = true;
          if (item.fields.displayRules?.length) {
            showItem = await executeDisplayRules(
              SSInsuranceProductDetailsPolicyDetailsRules,
              item.fields.displayRules,
              coverage
            );
          }
          return { item, showItem };
        })
      );

      const visibleItems = await Promise.all(
        itemChecks
          .filter(({ showItem }) => showItem)
          .map(async ({ item }) => {
            const showPrimaryLink = executeEditRules(
              SSInsuranceProductDetailsPolicyEditRules,
              item.fields.editRules,
              coverage
            );

            if (showPrimaryLink && item.fields.primaryLink.value.title) {
              item.fields.primaryLink.value.querystring = await updatePrimaryLinkQueryString(
                currentPolicy.value!.policyNumber!,
                item,
                coverage
              );
            }

            return {
              ...item,
              showPrimaryLink,
            } as ISitecoreReviewSummaryItem;
          })
      );

      return {
        ...field,
        items: visibleItems,
      };
    })
  );

  return x;
}

async function getPolicyDetailsSummaryModel(): Promise<any> {
  if (!currentPolicy.value) return null;

  let vehicleCount = 0;
  let propertycount = 0;
  let petCount = 0;

  for (const coverage of currentPolicy.value.coverages) {
    let reviewSummaryContentTemp: any;
    let reviewSummaryTemp: IReviewSummary[];
    let reviewSummaryDiscountsTemp: string[] = [];
    let showUpgradeInfoBoxForMotorTemp: boolean = false;
    let upgradeToComprehensiveLinkTemp: Link | null = null;

    if (coverage.isHome) {
      reviewSummaryContentTemp = getFormInput(props.content, 'homePolicyDetails');
    } else if (coverage.isPet) {
      reviewSummaryContentTemp = getFormInput(props.content, 'petPolicyDetails');
    } else {
      // isMotor and other are treated as motorPolicyDetails
      // Treat policy as 'other' (motorcycle, boat, caravan, etc)
      reviewSummaryContentTemp = getFormInput(props.content, 'motorPolicyDetails');

      // SSINSP.FR8.2
      if (coverage.isMotor && isUpgradeable(coverage as VehicleCoverage)) {
        showUpgradeInfoBoxForMotorTemp = true;
        try {
          const queryString = await updateQuoteSessionId(
            currentPolicy.value!.policyNumber!,
            upgradeToComprehensiveLinkItem.value.link.value.querystring,
            'upgradeMotorSessionId',
            SelfServiceSessionPages.UpgradeVehiclePage,
            coverage.id
          );
          if (!queryString) {
            upgradeToComprehensiveLinkTemp = null;
          } else {
            upgradeToComprehensiveLinkTemp = {
              title: 'Upgrade cover',
              url: `${upgradeToComprehensiveLinkItem.value.link.value.href}?${queryString}`,
              type: LinkTypes.LINK,
            };
          }
        } catch (error) {
          console.error('error', error);
          upgradeToComprehensiveLinkTemp = null;
        }
      }
    }

    const addDiscounts =
      coverage.discounts.length > 0 &&
      (coverage.isPet ||
        coverage.isHome ||
        isComprehensiveCarOrIsFireTheftAndThirdPartyCarOrIsThirdPartyPropertyDamageCar(coverage));

    if (addDiscounts) {
      // Add discount section if Pet or Home or the policy is for a motor vehicle (NOT motorcycle, caravan, trailer, etc.)
      reviewSummaryDiscountsTemp = coverage.discounts;
    }

    // Handle when there is more than one policy of the same type (e.g. 2 cars, 3 homes, 4 pets)
    if (currentPolicy.value.coverages.length > 1) {
      try {
        if (reviewSummaryContentTemp.name === 'petPolicyDetails') {
          petCount++;
          reviewSummaryContentTemp.fields[0].summaryTitle.value = `Pet ${numberToWord(petCount)?.toLowerCase()}`;
          reviewSummaryContentTemp.fields[0].icon.value = 'bg-pet-filled-icon';
        } else if (reviewSummaryContentTemp.name === 'homePolicyDetails') {
          propertycount++;
          reviewSummaryContentTemp.fields[0].summaryTitle.value = `Property ${numberToWord(propertycount)?.toLowerCase()}`;
          reviewSummaryContentTemp.fields[0].icon.value = 'bg-home-icon';
        } else if (reviewSummaryContentTemp.name === 'motorPolicyDetails') {
          vehicleCount++;
          reviewSummaryContentTemp.fields[0].summaryTitle.value = `Vehicle ${numberToWord(vehicleCount)?.toLowerCase()}`;

          if (currentPolicy.value.coverages[0].isBoat && hasSameProperty(currentPolicy.value.coverages, 'isBoat')) {
            reviewSummaryContentTemp.fields[0].icon.value = 'bg-boat-filled-icon';
          } else {
            reviewSummaryContentTemp.fields[0].icon.value = 'bg-car-filled-icon';
          }
        }
      } catch (error) {
        console.error('error', error);
      }
    }

    reviewSummaryTemp = await mapCoverage(coverage, reviewSummaryContentTemp.fields);

    policyDetailsData.value.push({
      reviewSummaryContent: reviewSummaryContentTemp,
      reviewSummaryDiscounts: reviewSummaryDiscountsTemp ?? [],
      reviewSummary: reviewSummaryTemp,
      showUpgradeInfoBoxForMotor: showUpgradeInfoBoxForMotorTemp,
      upgradeToComprehensiveLink: upgradeToComprehensiveLinkTemp,
    });
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(policyDetailsData.value, (item, itemIndex) => {
    return (_openBlock(), _createElementBlock("div", {
      key: itemIndex,
      class: "mt-12",
      "data-test-id": "policy_details_component"
    }, [
      _createVNode(ReviewSummary, {
        reviewSummaries: item.reviewSummary,
        isSelfService: true,
        title: { value: `` },
        heading: { value: `` },
        belowHeading: { value: `` },
        subheading: { value: `` },
        summaryItemFooterOnLastSummaryOnly: true,
        isConfirmationStep: policyDetailsData.value.length > 1
      }, _createSlots({
        ssPolicyDiscounts: _withCtx(() => [
          _createVNode(SSPolicyDiscounts, {
            discounts: item.reviewSummaryDiscounts,
            summaryHeading: "Your discounts",
            tagSize: _unref(TagSize).sm
          }, null, 8, ["discounts", "tagSize"])
        ]),
        _: 2
      }, [
        (item.showUpgradeInfoBoxForMotor && item.upgradeToComprehensiveLink)
          ? {
              name: "subContent-PolicyDetails-InsuranceType",
              fn: _withCtx(() => [
                _createElementVNode("div", _hoisted_1, [
                  _createVNode(InformationBox, {
                    description: upgradeToComprehensiveInfoBox.value.content.value,
                    title: upgradeToComprehensiveInfoBox.value.title.value,
                    type: _unref(InformationBoxTypes).INFO,
                    links: [item.upgradeToComprehensiveLink]
                  }, null, 8, ["description", "title", "type", "links"])
                ])
              ]),
              key: "0"
            }
          : undefined
      ]), 1032, ["reviewSummaries", "isConfirmationStep"])
    ]))
  }), 128))
}
}

})